/**
 * Delivery estimate, e.g. "Arrives Tue 13 – Mon 26 Oct".
 *
 * The server renders a business-day fallback ("Arrives in 5–14 business days") so the line is useful without
 * JavaScript and on cached pages. This element swaps in real dates counted from today, skipping weekends.
 * It re-applies the dates if a section re-render (morph) restores the fallback text.
 *
 * Attributes:
 * - data-min / data-max: business days from today
 * - data-template: translated text with a [range] placeholder
 */
class DeliveryEstimate extends HTMLElement {
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    this.#render();
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  #render() {
    const target = this.querySelector('[data-delivery-text]');
    const template = this.dataset.template;
    const min = Number.parseInt(this.dataset.min ?? '', 10);
    const max = Number.parseInt(this.dataset.max ?? '', 10);

    if (!target || !template || Number.isNaN(min) || Number.isNaN(max)) return;

    const text = template.replace('[range]', formatRange(addBusinessDays(min), addBusinessDays(Math.max(min, max))));

    // Guard so our own update doesn't re-trigger the observer endlessly
    if (target.textContent !== text) target.textContent = text;
  }
}

/**
 * @param {number} days - Business days to add to today
 * @returns {Date}
 */
function addBusinessDays(days) {
  const date = new Date();
  let added = 0;

  while (added < days) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }

  return date;
}

/**
 * "Tue 13 – Mon 26 Oct", or "Thu 22 Oct – Fri 13 Nov" when the months differ
 * @param {Date} start
 * @param {Date} end
 * @returns {string}
 */
function formatRange(start, end) {
  const locale = document.documentElement.lang || 'en-AU';
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const month = new Intl.DateTimeFormat(locale, { month: 'short' });
  const day = (/** @type {Date} */ date) => `${weekday.format(date)} ${date.getDate()}`;

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${day(start)} – ${day(end)} ${month.format(end)}`;
  }

  return `${day(start)} ${month.format(start)} – ${day(end)} ${month.format(end)}`;
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
